import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FaEnvelope, FaUser, FaUserTie, FaUserCog, FaBuilding } from 'react-icons/fa';
import { API_URL } from '../config';
import { getFreshSupabaseToken } from '../tokenRefresh';

function MessagesDropdown({ userId }) {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const dropdownRef = useRef(null);
  
  const user = JSON.parse(localStorage.getItem('user') || '{}');

  // Auth headers for all API calls (includes the RLS login card, auto-refreshed)
  const getAuthHeaders = async () => {
    const token = localStorage.getItem('token');
    const supabaseToken = await getFreshSupabaseToken();
    const headers = { 'Content-Type': 'application/json' };
    if (token) headers['Authorization'] = `Bearer ${token}`;
    if (supabaseToken) headers['x-supabase-token'] = supabaseToken;
    return headers;
  };

  useEffect(() => {
    if (userId) {
      fetchMessages();
    }
  }, [userId]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchMessages = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/messages?user_id=${userId}`, {
        headers: await getAuthHeaders()
      });
      const data = await response.json();
      if (data.success) {
        let filteredMessages = data.messages || [];
        
        // ✅ FIX: Filter based on user role
        const userData = JSON.parse(localStorage.getItem('user') || '{}');
        if (userData?.role === 'subcontractor' || userData?.role === 'subbie') {
          // Subbies can ONLY see messages from admins
          filteredMessages = filteredMessages.filter(m => 
            m.sender?.role === 'admin'
          );
        } else if (userData?.role === 'client') {
          // Clients can only see messages from admins
          filteredMessages = filteredMessages.filter(m => 
            m.sender?.role === 'admin'
          );
        }
        
        setMessages(filteredMessages);
        const unread = filteredMessages.filter(m => !m.is_read).length;
        setUnreadCount(unread);
      }
    } catch (err) {
      console.error('Error fetching messages:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleViewAll = () => {
    setIsOpen(false);
    // Navigate based on role
    if (user?.role === 'subcontractor' || user?.role === 'subbie') {
      navigate('/subbie-messages');
    } else if (user?.role === 'client') {
      navigate('/client-messages');
    } else {
      navigate('/messages');
    }
  };

  const handleMessageClick = (message) => {
    setIsOpen(false);
    if (user?.role === 'subcontractor' || user?.role === 'subbie') {
      navigate('/subbie-messages');
    } else if (user?.role === 'client') {
      navigate('/client-messages');
    } else {
      navigate('/messages');
    }
  };

  const markAsRead = async (messageId) => {
    try {
      await fetch(`${API_URL}/messages/${messageId}/read`, {
        method: 'PUT',
        headers: await getAuthHeaders(),
        body: JSON.stringify({ user_id: userId })
      });
      fetchMessages();
    } catch (err) {
      console.error('Error marking message as read:', err);
    }
  };

  const getRoleIcon = (role) => {
    switch (role) {
      case 'admin':
        return <FaUserCog className="text-purple-500" size={12} />;
      case 'client':
        return <FaBuilding className="text-blue-500" size={12} />;
      case 'subcontractor':
      case 'subbie':
        return <FaUserTie className="text-green-500" size={12} />;
      default:
        return <FaUser className="text-gray-400" size={12} />;
    }
  };

  const getRoleLabel = (role) => {
    switch (role) {
      case 'admin':
        return 'Admin';
      case 'client':
        return 'Client';
      case 'subcontractor':
      case 'subbie':
        return 'Subbie';
      default:
        return role || 'User';
    }
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return '';
    const now = new Date();
    const then = new Date(timestamp);
    const diff = Math.floor((now - then) / 1000);
    
    if (diff < 60) return 'just now';
    if (diff < 3600) return Math.floor(diff / 60) + 'm ago';
    if (diff < 86400) return Math.floor(diff / 3600) + 'h ago';
    return then.toLocaleDateString();
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="relative p-2 rounded-lg hover:bg-gray-100 transition-colors"
        style={{ color: '#0A1929' }}
      >
        <FaEnvelope size={20} />
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 px-1.5 py-0.5 text-xs font-bold rounded-full" style={{ backgroundColor: '#EF4444', color: 'white', minWidth: '18px' }}>
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-2 w-80 bg-white rounded-xl shadow-lg border z-50 max-h-96 overflow-y-auto" style={{ borderColor: '#E8ECEF' }}>
          <div className="flex justify-between items-center p-3 border-b" style={{ borderColor: '#E8ECEF' }}>
            <span className="font-medium text-sm" style={{ color: '#0A1929' }}>Messages</span>
            <button
              onClick={handleViewAll}
              className="text-xs hover:underline flex items-center gap-1" 
              style={{ color: '#00A86B' }}
            >
              View All →
            </button>
          </div>

          {loading ? (
            <div className="p-4 text-center">
              <span className="text-sm" style={{ color: '#94A3B8' }}>Loading...</span>
            </div>
          ) : messages.length === 0 ? (
            <div className="p-6 text-center">
              <div className="text-3xl mb-2">💬</div>
              <p className="text-sm" style={{ color: '#94A3B8' }}>No messages</p>
            </div>
          ) : (
            <div className="divide-y" style={{ borderColor: '#E8ECEF' }}>
              {messages.slice(0, 5).map((message) => (
                <div 
                  key={message.id} 
                  className={`p-3 hover:bg-gray-50 transition-colors cursor-pointer ${!message.is_read ? 'bg-blue-50' : ''}`}
                  onClick={() => {
                    if (!message.is_read) markAsRead(message.id);
                    handleMessageClick(message);
                  }}
                >
                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0">
                      <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center text-sm font-semibold text-gray-600">
                        {message.sender?.full_name?.charAt(0) || 'S'}
                      </div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-medium truncate" style={{ color: '#0A1929' }}>
                          {message.sender?.full_name || 'System'}
                        </p>
                        <span className="flex items-center gap-1 text-xs text-gray-400">
                          {getRoleIcon(message.sender?.role)}
                          <span>{getRoleLabel(message.sender?.role)}</span>
                        </span>
                      </div>
                      <p className="text-xs truncate" style={{ color: '#4A5568' }}>{message.message || message.content}</p>
                      <p className="text-xs mt-1" style={{ color: '#94A3B8' }}>{formatTime(message.created_at)}</p>
                    </div>
                    {!message.is_read && (
                      <div className="w-2 h-2 rounded-full flex-shrink-0 mt-1" style={{ backgroundColor: '#3B82F6' }}></div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default MessagesDropdown;